import { Backdrop, IconButton, Stack, SxProps, Theme, Typography } from '@mui/material';
import {
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizable } from 'react-resizable-layout';
import { isNullish } from 'remeda';
import { DynamicIcon, Tooltip } from 'src/components';
import { usePhoneNavInPaneHeader } from 'src/components/Navbar/use-phone-nav-in-pane-header';
import { useSelectedPartner } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import useNavigationStore from 'src/store/navigation';
import { DEFAULT_NAVBAR_WIDTH_PX } from 'src/store/settings';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';
import { AppMenu } from './AppMenu/app-menu';
import { DesktopDrawer } from './DesktopDrawer';
import {
  MobileHeaderNavContainer,
  NavbarHoverTrap,
  NavbarResizeHandle,
  NavigationSidePanel,
} from './navbar.styles';
import { NavigationDataItem, useNavigationItems } from './use-navigation-items';
import { useOpenSidekickParam } from './use-open-sidekick-param';
import { usePaneRouteSync, useRoutePane } from './use-pane-route-sync';

const MobileNavbar = ({
  onMobileMenuOpen,
}: {
  onMobileMenuOpen: (event: MouseEvent<HTMLElement>) => void;
}) => (
  <MobileHeaderNavContainer>
    <Tooltip title="App menu">
      <IconButton
        sx={{ position: 'absolute', left: 0 }}
        disableRipple
        size="large"
        onClick={onMobileMenuOpen}
      >
        <DynamicIcon name="menu" />
      </IconButton>
    </Tooltip>
    <Typography sx={{ fontSize: '1.375rem' }}>ProVision</Typography>
  </MobileHeaderNavContainer>
);

export const NavigationPanel = ({
  isOpen,
  toggleNavbar,
  sx,
  isPeeking,
  onMouseEnter,
  onMouseLeave,
  navWidthPx,
}: {
  isOpen: boolean;
  toggleNavbar: () => void;
  sx?: SxProps<Theme>;
  isPeeking?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  navWidthPx?: number;
}) => {
  const { isMobile } = useScreenDimensions();
  const isNavInPaneHeader = usePhoneNavInPaneHeader();
  const hasMobileTopBar = isMobile && !isNavInPaneHeader;
  const { navigate, pathnameWithoutPrefix } = useNavigateRoute();

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { selectedPartner } = useSelectedPartner();

  const [appMenuAnchor, setAppMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const navigateToItem = useCallback(
    (item: NavigationDataItem) => {
      navigate(item.path);
      if (isMobile) toggleNavbar();
    },
    [navigate, isMobile, toggleNavbar],
  );

  const { navigationGroups, settingsNavigationGroups } = useNavigationItems();

  const activePane = useNavigationStore((state) => state.activePane);
  const setActivePane = useNavigationStore((state) => state.setActivePane);

  const routePane = useRoutePane({
    navigationGroups,
    settingsNavigationGroups,
    pathname: pathnameWithoutPrefix,
  });

  usePaneRouteSync({ routePane, pathname: pathnameWithoutPrefix });
  useOpenSidekickParam();

  const handleAppMenuOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => setAppMenuAnchor(event.currentTarget),
    [],
  );

  const closeMenus = useCallback(() => {
    setAppMenuAnchor(null);
    setMobileMenuAnchor(null);
  }, []);

  return (
    <NavigationSidePanel sx={sx} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <DesktopDrawer
        title={
          !isNullish(selectedPartnerId)
            ? (selectedPartner?.businessName ?? '')
            : 'Multi-Tenancy'
        }
        onAppMenuOpen={handleAppMenuOpen}
        onNavigateToItem={navigateToItem}
        navigationGroups={navigationGroups}
        settingsNavigationGroups={settingsNavigationGroups}
        activePane={activePane}
        onPaneChange={setActivePane}
        isDrawerOpen={isOpen || Boolean(mobileMenuAnchor)}
        toggleDrawer={toggleNavbar}
        isPeeking={isPeeking}
        navWidthPx={navWidthPx}
        hasMobileTopBar={hasMobileTopBar}
      />
      {hasMobileTopBar ? <MobileNavbar onMobileMenuOpen={toggleNavbar} /> : null}

      <AppMenu anchor={appMenuAnchor} onClose={closeMenus} />
    </NavigationSidePanel>
  );
};

const NAVBAR_Z_INDEX = 4;
const OVERLAY_Z_INDEX = 3;
const MIN_NAVBAR_WIDTH_PX = 260;
const MAX_NAVBAR_WIDTH_PX = 400;

export const Navbar = () => {
  const navbar = useGlobalStore.use.navbar();
  const setNavbar = useGlobalStore.use.setNavbar();
  const { isMobile, isDesktop } = useScreenDimensions();
  const [navbarHovered, setNavbarHovered] = useState(false);
  const [skipDockTransition, setSkipDockTransition] = useState(false);

  const navbarOpened = navbar.isOpen;
  const toggleNavbar = useCallback(() => {
    setNavbar({ isOpen: !navbarOpened });
    if (navbarOpened) {
      setNavbarHovered(false);
    }
  }, [navbarOpened, setNavbar]);

  // small delay on close avoids flicker when moving from the trap onto the peeked panel
  const peekCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openPeek = useCallback(() => {
    if (peekCloseTimeoutRef.current) {
      clearTimeout(peekCloseTimeoutRef.current);
      peekCloseTimeoutRef.current = null;
    }
    setNavbarHovered(true);
  }, []);
  const scheduleClosePeek = useCallback(() => {
    if (peekCloseTimeoutRef.current) clearTimeout(peekCloseTimeoutRef.current);
    peekCloseTimeoutRef.current = setTimeout(() => setNavbarHovered(false), 250);
  }, []);
  const openAndFixateNav = useCallback(() => {
    if (peekCloseTimeoutRef.current) {
      clearTimeout(peekCloseTimeoutRef.current);
      peekCloseTimeoutRef.current = null;
    }
    setNavbarHovered(false);
    setNavbar({ isOpen: true });
    setSkipDockTransition(true);
  }, [setNavbar]);
  useEffect(
    () => () => {
      if (peekCloseTimeoutRef.current) clearTimeout(peekCloseTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!skipDockTransition) return undefined;
    const id = requestAnimationFrame(() => setSkipDockTransition(false));
    return () => cancelAnimationFrame(id);
  }, [skipDockTransition]);

  const isPeeking = isDesktop && !navbarOpened && navbarHovered;

  const navbarContainerRef = useRef<HTMLDivElement | null>(null);
  const { position, setPosition, separatorProps, isDragging } = useResizable({
    axis: 'x',
    initial: navbar.width,
    min: MIN_NAVBAR_WIDTH_PX,
    max: MAX_NAVBAR_WIDTH_PX,
    containerRef: navbarContainerRef as RefObject<HTMLElement>,
    onResizeEnd: ({ position }) => {
      setNavbar({ width: position });
    },
  });

  const { containerSx, navbarSx } = useMemo(() => {
    if (isMobile) {
      return {
        containerSx: {
          flexShrink: 0,
        },
        navbarSx: {
          zIndex: NAVBAR_Z_INDEX,
          transition: 'transform 0.3s ease-in',
        },
      };
    }

    /**
     * When open, the navbar occupies its resizable width in the layout as normal.
     * When collapsed, it takes zero layout space; a fixed hover trap at the window
     * edge reveals it as a floating overlay (see NavbarHoverTrap / isPeeking).
     */
    return {
      containerSx: {
        position: 'relative',
        maxWidth: navbarOpened ? position : 0,
        width: navbarOpened ? position : 0,
        userSelect: isDragging ? 'none' : 'auto',
        flexShrink: 0,
        ...(navbarOpened && !isDragging && !skipDockTransition
          ? { transition: 'max-width 0.3s ease-in, width 0.3s ease-in' }
          : {}),
      },
      navbarSx: navbarOpened
        ? {
            zIndex: NAVBAR_Z_INDEX,
          }
        : {
            zIndex: NAVBAR_Z_INDEX,
            width: 0,
            opacity: navbarHovered ? 1 : 0,
            pointerEvents: navbarHovered ? ('auto' as const) : ('none' as const),
          },
    };
  }, [isDragging, isMobile, navbarHovered, navbarOpened, position, skipDockTransition]);

  return (
    <Stack direction="row" ref={navbarContainerRef} sx={containerSx}>
      <NavigationPanel
        isOpen={navbarOpened}
        toggleNavbar={toggleNavbar}
        sx={navbarSx}
        isPeeking={isPeeking}
        onMouseEnter={!isMobile && !navbarOpened ? openPeek : undefined}
        onMouseLeave={!isMobile && !navbarOpened ? scheduleClosePeek : undefined}
        navWidthPx={navbar.width}
      />
      {isMobile && navbarOpened ? (
        <Backdrop open sx={{ zIndex: OVERLAY_Z_INDEX }} onClick={toggleNavbar} />
      ) : null}
      {isDesktop && navbarOpened ? (
        <NavbarResizeHandle
          isDragging={isDragging}
          sx={{
            zIndex: NAVBAR_Z_INDEX + 1,
          }}
          {...separatorProps}
          onDoubleClick={() => {
            setPosition(DEFAULT_NAVBAR_WIDTH_PX);
            setNavbar({ width: DEFAULT_NAVBAR_WIDTH_PX });
          }}
        />
      ) : null}
      {isDesktop && !navbarOpened ? (
        <NavbarHoverTrap
          onMouseEnter={openPeek}
          onMouseLeave={scheduleClosePeek}
          onClick={openAndFixateNav}
        />
      ) : null}
    </Stack>
  );
};
